/**
 * Stepper horizontal réutilisable pour formulaires multi-étapes.
 * Affiche des cercles numérotés reliés par des lignes de progression.
 */

import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import type { LucideIcon } from "lucide-react";

export interface StepperStep {
  id: number;
  label: string;
  icon: LucideIcon;
}

interface StepperIndicatorProps {
  steps: StepperStep[];
  currentStep: number;
  onStepClick: (stepId: number) => void;
  maxVisitedStep: number;
}

export function StepperIndicator({ steps, currentStep, onStepClick, maxVisitedStep }: StepperIndicatorProps) {
  return (
    <div className="w-full max-w-sm mx-auto mb-6">
      <div className="flex items-center justify-between relative">
        {/* Ligne de fond grise */}
        <div className="absolute left-0 right-0 top-1/2 h-0.5 bg-muted -translate-y-1/2 z-0" />

        {/* Ligne de progression active */}
        <div
          className="absolute left-0 top-1/2 h-0.5 bg-primary -translate-y-1/2 z-0 transition-all duration-300"
          style={{
            width: `${Math.min(((currentStep - 1) / (steps.length - 1)) * 100, 100)}%`,
          }}
        />

        {/* Cercles des étapes */}
        {steps.map((step) => {
          const isCompleted = step.id < currentStep;
          const isActive = step.id === currentStep;
          const isClickable = step.id <= maxVisitedStep;

          return (
            <div
              key={step.id}
              className="relative z-10 flex flex-col items-center"
            >
              <button
                type="button"
                onClick={() => isClickable && onStepClick(step.id)}
                className={cn(
                  "w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 border-2",
                  isCompleted && "bg-primary border-primary text-primary-foreground",
                  isActive && "bg-primary/10 border-primary text-primary",
                  !isCompleted && !isActive && "bg-background border-muted text-muted-foreground",
                  isClickable ? "cursor-pointer" : "cursor-default",
                )}
                tabIndex={isClickable ? 0 : -1}
              >
                {isCompleted ? (
                  <Check className="w-5 h-5" />
                ) : (
                  <span className="text-sm font-semibold">{step.id}</span>
                )}
              </button>

              <span
                className={cn(
                  "mt-2 text-xs font-medium text-center",
                  (isCompleted || isActive) ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default StepperIndicator;
